import type { BladeShape, HeatMap } from '../types';

/** Map a heat value (0-1+) to an RGB triple modeling steel forge color. */
export function heatToColor(h: number): [number, number, number] {
  // Clamp to a sane range for color sampling.
  const t = Math.max(0, Math.min(1.3, h));
  // Stops (heat, R, G, B):
  // 0.00 -> #1a1818 (near-black, faint gray)
  // 0.20 -> #4a0d05 (dark cherry red)
  // 0.40 -> #c4360b (bright orange-red)
  // 0.60 -> #f08020 (yellow-orange)
  // 0.80 -> #ffd478 (white-yellow)
  // 1.00 -> #e8ecff (blue-white peak)
  // 1.30 -> #6a728a (burnt blue-gray)
  const stops: [number, number, number, number][] = [
    [0.0, 26, 24, 24],
    [0.2, 74, 13, 5],
    [0.4, 196, 54, 11],
    [0.6, 240, 128, 32],
    [0.8, 255, 212, 120],
    [1.0, 232, 236, 255],
    [1.3, 106, 114, 138],
  ];
  for (let i = 1; i < stops.length; i++) {
    const a = stops[i - 1]!;
    const b = stops[i]!;
    if (t <= b[0]) {
      const k = (t - a[0]) / (b[0] - a[0]);
      return [
        Math.round(a[1] + (b[1] - a[1]) * k),
        Math.round(a[2] + (b[2] - a[2]) * k),
        Math.round(a[3] + (b[3] - a[3]) * k),
      ];
    }
  }
  const last = stops[stops.length - 1]!;
  return [last[1], last[2], last[3]];
}

/** Sample heat at a position 0-1 along the blade with linear interpolation. */
export function sampleHeat(map: HeatMap, t: number): number {
  const n = map.samples.length;
  if (n === 0) return 0;
  const idxF = t * (n - 1);
  const i = Math.floor(idxF);
  const f = idxF - i;
  const a = map.samples[Math.max(0, Math.min(n - 1, i))]!;
  const b = map.samples[Math.max(0, Math.min(n - 1, i + 1))]!;
  return a + (b - a) * f;
}

export interface DrawBladeOpts {
  /** Center of the blade in logical coords. */
  cx: number;
  cy: number;
  /** Length in logical px. */
  length: number;
  /** Rotation in radians. 0 = pommel left, tip right. */
  rotation: number;
  /** Optional bend along the spine (0 = straight, positive = bow toward +y in blade frame). */
  bend?: number;
  /** Damage marks: positions 0-1 along blade where small chips are drawn. */
  damage?: number[];
}

/** Draws blade silhouette using vertical strips colored by local heat for a smooth gradient. */
export function drawBlade(
  ctx: CanvasRenderingContext2D,
  shape: BladeShape,
  heat: HeatMap,
  opts: DrawBladeOpts,
): void {
  const { cx, cy, length, rotation, bend = 0, damage = [] } = opts;
  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);

  // Blade local frame: x in [0,1] mapped to [-length/2, +length/2],
  // y units are also fractions of length.
  const halfLen = length / 2;
  const toLocalX = (x: number) => (x - 0.5) * length;
  const toLocalY = (y: number, xAlong: number) => {
    // Bend: parabolic offset peaking at center.
    const k = 4 * xAlong * (1 - xAlong);
    return y * length + bend * length * 0.15 * k;
  };

  // Strip rendering: slice along blade x in many narrow strips, color each
  // by sampled heat. Strips are clipped to the polygon for the silhouette.
  const strips = 80;
  ctx.save();

  // Build polygon path for clipping.
  ctx.beginPath();
  const verts = shape.vertices;
  for (let i = 0; i < verts.length; i++) {
    const v = verts[i]!;
    const lx = toLocalX(v.x);
    const ly = toLocalY(v.y, v.x);
    if (i === 0) ctx.moveTo(lx, ly);
    else ctx.lineTo(lx, ly);
  }
  ctx.closePath();
  ctx.clip();

  for (let i = 0; i < strips; i++) {
    const t0 = i / strips;
    const t1 = (i + 1) / strips;
    const tMid = (t0 + t1) * 0.5;
    const h = sampleHeat(heat, tMid);
    const [r, g, b] = heatToColor(h);
    ctx.fillStyle = `rgb(${r},${g},${b})`;
    const x0 = (t0 - 0.5) * length;
    const x1 = (t1 - 0.5) * length;
    // Overdraw vertically; the clip restricts it to the blade shape.
    ctx.fillRect(x0 - 0.5, -length, x1 - x0 + 1, length * 2);
  }

  // Subtle bevel highlight along spine (a thin lighter line near y=0).
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = 'rgba(255,255,255,0.06)';
  ctx.fillRect(-halfLen, -1, length, 1.5);
  ctx.globalCompositeOperation = 'source-over';

  ctx.restore();

  // Outline.
  ctx.beginPath();
  for (let i = 0; i < verts.length; i++) {
    const v = verts[i]!;
    const lx = toLocalX(v.x);
    const ly = toLocalY(v.y, v.x);
    if (i === 0) ctx.moveTo(lx, ly);
    else ctx.lineTo(lx, ly);
  }
  ctx.closePath();
  ctx.lineWidth = 1.2;
  ctx.strokeStyle = 'rgba(0,0,0,0.55)';
  ctx.stroke();

  // Damage marks (small notches on the edge).
  for (const d of damage) {
    const lx = toLocalX(d);
    const ly = toLocalY(0.09, d);
    ctx.fillStyle = '#1a0e08';
    ctx.beginPath();
    ctx.moveTo(lx - 2, ly - 3);
    ctx.lineTo(lx + 2, ly - 3);
    ctx.lineTo(lx, ly + 2);
    ctx.closePath();
    ctx.fill();
  }

  // Hilt (a simple grip + crossguard at x=0 side).
  drawHilt(ctx, -halfLen, length);

  ctx.restore();

  // Glow pass: drawn on top of original transform but additive.
  drawGlow(ctx, shape, heat, opts);
}

function drawHilt(ctx: CanvasRenderingContext2D, hiltX: number, length: number): void {
  // Crossguard
  ctx.fillStyle = '#3a2a1a';
  ctx.strokeStyle = '#1a0f08';
  ctx.lineWidth = 1;
  const cgW = length * 0.04;
  const cgH = length * 0.14;
  ctx.fillRect(hiltX - cgW, -cgH / 2, cgW * 2, cgH);
  ctx.strokeRect(hiltX - cgW, -cgH / 2, cgW * 2, cgH);
  // Grip
  ctx.fillStyle = '#5a3a22';
  const gripW = length * 0.16;
  const gripH = length * 0.05;
  ctx.fillRect(hiltX - gripW, -gripH / 2, gripW, gripH);
  ctx.strokeRect(hiltX - gripW, -gripH / 2, gripW, gripH);
  // Pommel
  ctx.fillStyle = '#7a5a30';
  ctx.beginPath();
  ctx.arc(hiltX - gripW, 0, gripH * 0.7, 0, Math.PI * 2);
  ctx.fill();
  ctx.stroke();
}

function drawGlow(
  ctx: CanvasRenderingContext2D,
  shape: BladeShape,
  heat: HeatMap,
  opts: DrawBladeOpts,
): void {
  const { cx, cy, length, rotation } = opts;
  // Average heat governs the overall glow halo.
  let avg = 0;
  for (const s of heat.samples) avg += s;
  avg /= Math.max(1, heat.samples.length);
  if (avg < 0.3) return;
  const intensity = Math.min(1, (avg - 0.3) / 0.6);

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(rotation);
  ctx.globalCompositeOperation = 'lighter';

  // Hot strips also leak a per-section glow above/below the blade.
  const strips = 24;
  for (let i = 0; i < strips; i++) {
    const tMid = (i + 0.5) / strips;
    const h = sampleHeat(heat, tMid);
    if (h < 0.4) continue;
    const [r, g, b] = heatToColor(h);
    const x = (tMid - 0.5) * length;
    // Find approximate edge y at this x (use widest vertex magnitude as fallback).
    const edgeY = edgeYAt(shape, tMid);
    const halo = (h - 0.3) * 18 * intensity;
    const grad = ctx.createRadialGradient(x, 0, 1, x, 0, halo + 6);
    grad.addColorStop(0, `rgba(${r},${g},${b},${0.35 * intensity})`);
    grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
    ctx.fillStyle = grad;
    ctx.fillRect(x - halo - 6, -edgeY * length - halo - 6, (halo + 6) * 2, (edgeY * length + halo + 6) * 2);
  }
  ctx.restore();
}

function edgeYAt(shape: BladeShape, t: number): number {
  // Find the max |y| at the nearest vertex; rough estimate is fine for glow halo.
  let best = 0;
  let bestDx = Infinity;
  for (const v of shape.vertices) {
    const dx = Math.abs(v.x - t);
    if (dx < bestDx) {
      bestDx = dx;
      best = Math.abs(v.y);
    }
  }
  return best + 0.02;
}
